import { Link, useRouterState } from '@tanstack/react-router';
import { Fragment, useMemo, type ReactNode } from 'react';
import { parseRef, ref as refs, type AnalyticsPage, type RefTarget } from '@/lib/assistant/refs';

/**
 * A model's answer as React elements, from a small markdown subset: `#` headings, `-`/`*`/`1.`
 * lists, `**bold**` and paragraphs. Model output is untrusted (the names in a brief are external
 * input and can carry a prompt injection), so nothing is ever rendered as HTML, and the only links
 * are `[text](ref)` whose ref parses AND was listed by the brief the model answered from. Everything
 * else, URLs included, is text.
 */

export type Block =
  | { type: 'heading'; text: string }
  | { type: 'list'; ordered: boolean; items: string[] }
  | { type: 'paragraph'; text: string };

export function parseBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  const flushParagraph = () => {
    if (paragraph.length) blocks.push({ type: 'paragraph', text: paragraph.join(' ') });
    paragraph = [];
  };
  const flushList = () => {
    if (list) blocks.push({ type: 'list', ...list });
    list = null;
  };
  for (const line of text.replace(/\r\n?/g, '\n').split('\n')) {
    const trimmed = line.trim();
    if (!trimmed || /^([-*_])(\s*\1){2,}$/.test(trimmed)) {
      flushParagraph();
      flushList();
      continue;
    }
    const heading = /^#{1,6}\s+(.+?)\s*#*$/.exec(trimmed);
    if (heading?.[1]) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'heading', text: heading[1] });
      continue;
    }
    const item = /^(?:([-*•+])|(\d{1,3})[.)])\s+(.*)$/.exec(trimmed);
    if (item) {
      flushParagraph();
      const ordered = item[2] !== undefined;
      if (!list || list.ordered !== ordered) {
        flushList();
        list = { ordered, items: [] };
      }
      list.items.push(item[3] ?? '');
      continue;
    }
    if (list && /^\s/.test(line)) {
      // An indented line continues the item above it.
      list.items[list.items.length - 1] += ` ${trimmed}`;
      continue;
    }
    flushList();
    paragraph.push(trimmed);
  }
  flushParagraph();
  flushList();
  return blocks;
}

/** A model may copy a title ref unencoded (`perf:Keno 97`); anything else must match exactly. */
function canonicalRef(destination: string): string {
  const value = destination.trim().replace(/^<(.*)>$/, '$1');
  if (!value.startsWith('perf:')) return value;
  const key = value.slice(5);
  try {
    return refs.perf(decodeURIComponent(key));
  } catch {
    return refs.perf(key);
  }
}

const LINK = 'font-medium text-foreground underline underline-offset-4 hover:text-primary';

/** Where a `page:` ref goes. */
export const PAGE_PATHS = {
  overview: '/analytics',
  games: '/analytics/games',
  operators: '/analytics/operators',
  alerts: '/analytics/alerts',
  explore: '/analytics/explore',
  players: '/analytics/players',
  retention: '/analytics/players/retention',
  funnel: '/analytics/players/funnel',
  playerValue: '/analytics/players/value',
} as const satisfies Record<AnalyticsPage, string>;

/** The filter row every analytics page shares (`analyticsFilterShape` in features/analytics/filters.ts). */
export const ANALYTICS_FILTER_KEYS = [
  'period',
  'from',
  'to',
  'compare',
  'operatorIds',
  'brandIds',
  'gameIds',
  'titleKeys',
  'currencies',
  'roundType',
  'includeTestPlayers',
] as const;

/** From an analytics page the link keeps the filters, but not that page's own settings (metric, cohort…). */
function PageLink({ page, children }: { page: AnalyticsPage; children: ReactNode }) {
  const fromAnalytics = useRouterState({ select: (s) => /^\/analytics(\/|$)/.test(s.location.pathname) });
  const search = (prev: Record<string, unknown>) =>
    fromAnalytics
      ? Object.fromEntries(ANALYTICS_FILTER_KEYS.filter((k) => prev[k] !== undefined).map((k) => [k, prev[k]]))
      : {};
  return (
    <Link to={PAGE_PATHS[page]} search={search as never} className={LINK}>
      {children}
    </Link>
  );
}

function RefLink({ target, children }: { target: RefTarget; children: ReactNode }) {
  switch (target.kind) {
    case 'game':
      return (
        <Link to="/games/$gameId" params={{ gameId: String(target.id) }} className={LINK}>
          {children}
        </Link>
      );
    case 'config':
      return (
        <Link to="/configs" search={{ game: target.id }} className={LINK}>
          {children}
        </Link>
      );
    case 'perf':
      return (
        <Link to="/analytics/games/$titleKey" params={{ titleKey: target.titleKey }} className={LINK}>
          {children}
        </Link>
      );
    case 'operator':
      return (
        <Link to="/operators/$operatorId" params={{ operatorId: String(target.id) }} className={LINK}>
          {children}
        </Link>
      );
    case 'operatorPerf':
      return (
        <Link to="/analytics/operators/$operatorId" params={{ operatorId: String(target.id) }} className={LINK}>
          {children}
        </Link>
      );
    case 'brand':
      return (
        <Link to="/brands/$brandId" params={{ brandId: String(target.id) }} className={LINK}>
          {children}
        </Link>
      );
    case 'alerts':
      return (
        <Link to="/analytics/alerts" className={LINK}>
          {children}
        </Link>
      );
    case 'page':
      return <PageLink page={target.page}>{children}</PageLink>;
  }
}

// A group of refs in parentheses, not a link's destination: `Crash (game:130, config:130)`.
const BARE_REFS = /\s*(?<!\])\(([^()\n]{1,400})\)/g;

/**
 * Refs are for links; a model that copies the brief's tags as they are would leave `(game:130)` in
 * the text. A parenthesised group made only of refs is dropped (it's never a link either way).
 */
export function stripBareRefs(text: string): string {
  return text.replace(BARE_REFS, (whole, inner: string) => {
    const tokens = inner.split(',').map((t) => t.trim());
    return tokens.every((t) => t !== '' && parseRef(t) !== null) ? '' : whole;
  });
}

// `**bold**`, or `[text](destination)` (one level of parentheses inside, as CommonMark allows).
// Link text is plain; bold may hold links.
const INLINE = /\*\*(.+?)\*\*|\[([^\]\n]+)\]\(((?:[^()\n]|\([^()\n]*\))*)\)/g;

function renderInline(text: string, allowed: ReadonlySet<string>, key = ''): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(INLINE)) {
    const index = match.index;
    if (index > last) out.push(text.slice(last, index));
    const [, bold, label, destination] = match;
    const k = `${key}${index}`;
    if (bold !== undefined) {
      out.push(<strong key={k}>{renderInline(bold, allowed, `${k}.`)}</strong>);
    } else if (label !== undefined) {
      const value = canonicalRef(destination ?? '');
      const target = allowed.has(value) ? parseRef(value) : null;
      out.push(
        target ? (
          <RefLink key={k} target={target}>
            {label}
          </RefLink>
        ) : (
          <Fragment key={k}>{label}</Fragment>
        ),
      );
    }
    last = index + match[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function AnswerText({ text, refs: allowedRefs }: { text: string; refs: readonly string[] }) {
  const allowed = useMemo(() => new Set(allowedRefs), [allowedRefs]);
  const blocks = useMemo(() => parseBlocks(stripBareRefs(text)), [text]);
  return (
    <div className="space-y-2 text-sm leading-relaxed break-words">
      {blocks.map((block, i) => {
        if (block.type === 'heading') {
          return (
            <h3 key={i} className="pt-1 text-sm font-semibold first:pt-0">
              {renderInline(block.text, allowed)}
            </h3>
          );
        }
        if (block.type === 'list') {
          const Tag = block.ordered ? 'ol' : 'ul';
          return (
            <Tag key={i} className={block.ordered ? 'list-decimal space-y-1 pl-5' : 'list-disc space-y-1 pl-5'}>
              {block.items.map((item, j) => (
                <li key={j}>{renderInline(item, allowed)}</li>
              ))}
            </Tag>
          );
        }
        return <p key={i}>{renderInline(block.text, allowed)}</p>;
      })}
    </div>
  );
}
